import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Shield, Save, Loader2, Plus, Trash2, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { API_BASE as API } from '../config';

const EMPTY_POLICIES = { al: [], pd: [], cd: [], ocac: [], wc: [] };

const LOSS_TYPES = [
  { key: 'al', label: 'Auto Liability' },
  { key: 'pd', label: 'Physical Damage' },
  { key: 'cd', label: 'Cargo Damage' },
  { key: 'ocac', label: 'Occupational Accident' },
  { key: 'wc', label: 'Workers Compensation' },
];

const isCurrent = (p) => {
  if (!p.start_date || !p.end_date) return false;
  const today = new Date().toISOString().slice(0, 10);
  return p.start_date <= today && today <= p.end_date;
};

export default function InsurancePolicies() {
  const { user, hasRole, operatorContext, selectedTenants } = useAuth();
  const isOperator = user?.is_platform_operator;
  const inContext = isOperator && operatorContext?.tenantId;
  const canEdit = hasRole('manager', 'admin', 'super_admin');

  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [policies, setPolicies] = useState(EMPTY_POLICIES);
  const [saving, setSaving] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({});

  // Load companies (scoped to current user via backend)
  useEffect(() => {
    if (isOperator && !inContext) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/companies`, { withCredentials: true });
        const names = (res.data || []).map((c) => c.name).sort();
        setCompanyList(names);
        if (names.length > 0) setSelectedCompany((prev) => prev || names[0]);
      } catch { /* ignore */ }
    };
    load();
  }, [isOperator, inContext, selectedTenants, operatorContext?.tenantId]);

  // Load policies for selected company
  useEffect(() => {
    if (!selectedCompany) { setPolicies(EMPTY_POLICIES); return; }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/company-insurance-policies`, { params: { company_name: selectedCompany }, withCredentials: true });
        setPolicies({ ...EMPTY_POLICIES, ...(res.data || {}) });
      } catch { setPolicies(EMPTY_POLICIES); }
    };
    load();
  }, [selectedCompany]);

  const addPolicy = (typeKey) => {
    setPolicies((prev) => ({
      ...prev,
      [typeKey]: [...(prev[typeKey] || []), { id: Math.random().toString(36).slice(2, 10), carrier_name: '', policy_no: '', start_date: '', end_date: '' }],
    }));
  };

  const updatePolicy = (typeKey, idx, field, value) => {
    setPolicies((prev) => {
      const arr = [...(prev[typeKey] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [typeKey]: arr };
    });
  };

  const removePolicy = (typeKey, idx) => {
    setPolicies((prev) => ({
      ...prev,
      [typeKey]: (prev[typeKey] || []).filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    if (!selectedCompany) { toast.error('Select a company first'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/company-insurance-policies`, { ...policies, company_name: selectedCompany }, { withCredentials: true });
      toast.success(`Insurance policies saved for ${selectedCompany}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save insurance policies');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="insurance-policies-page">
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-teal-500" />
          Insurance Policies
        </h1>
        <p className="text-sm text-gray-500 mt-1">Policy numbers and coverage periods by company</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Policies</CardTitle>
          <CardDescription>
            {canEdit ? 'Pick a company to view or edit its policies.' : 'You do not have permission to edit insurance policies.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <Label className="text-sm shrink-0">Company</Label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="flex-1 h-9 text-sm border border-gray-200 rounded-md px-2 bg-white"
              data-testid="insurance-company-selector"
            >
              {companyList.length === 0 && <option value="">No companies</option>}
              {companyList.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {selectedCompany && LOSS_TYPES.map(({ key, label }) => {
            const list = policies[key] || [];
            const currentPolicy = list.find((p) => isCurrent(p));
            const historicalPolicies = list.filter((p) => !isCurrent(p));
            return (
              <div key={key} className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden mb-3" data-testid={`insurance-${key}`}>
                <div className="flex items-center justify-between px-3 pt-3 pb-1">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  {canEdit && (
                    <button type="button" onClick={() => addPolicy(key)} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800" data-testid={`insurance-${key}-add`}>
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  )}
                </div>

                {/* Current Policy */}
                {currentPolicy ? (
                  <div className="mx-3 mb-2 p-2 rounded border border-teal-200 bg-teal-50/60">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">Current</span>
                      {canEdit && (
                        <button type="button" onClick={() => removePolicy(key, list.indexOf(currentPolicy))} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input type="text" placeholder="Carrier" disabled={!canEdit} value={currentPolicy.carrier_name || ''} onChange={(e) => updatePolicy(key, list.indexOf(currentPolicy), 'carrier_name', e.target.value)} className="h-7 text-xs bg-white flex-1 min-w-[100px]" data-testid={`insurance-${key}-current-carrier`} />
                      <Input type="text" placeholder="Policy #" disabled={!canEdit} value={currentPolicy.policy_no || ''} onChange={(e) => updatePolicy(key, list.indexOf(currentPolicy), 'policy_no', e.target.value)} className="h-7 text-xs bg-white w-28" data-testid={`insurance-${key}-current-policy`} />
                      <span className="text-[10px] text-gray-400 shrink-0">Start</span>
                      <Input type="date" disabled={!canEdit} value={currentPolicy.start_date || ''} onChange={(e) => updatePolicy(key, list.indexOf(currentPolicy), 'start_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                      <span className="text-[10px] text-gray-400 shrink-0">End</span>
                      <Input type="date" disabled={!canEdit} value={currentPolicy.end_date || ''} onChange={(e) => updatePolicy(key, list.indexOf(currentPolicy), 'end_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                    </div>
                  </div>
                ) : list.length === 0 ? (
                  <p className="px-3 pb-3 text-xs text-gray-400 italic">No policies added</p>
                ) : (
                  <p className="mx-3 mb-2 text-xs text-amber-600 italic">No current policy — all policies are expired or future-dated</p>
                )}

                {/* If no current, show all editable */}
                {!currentPolicy && list.length > 0 && (
                  <div className="mx-3 mb-2 space-y-1.5">
                    {list.map((p, idx) => (
                      <div key={p.id || idx} className="flex items-center gap-2 p-2 rounded border border-gray-200 bg-white">
                        <Input type="text" placeholder="Carrier" disabled={!canEdit} value={p.carrier_name || ''} onChange={(e) => updatePolicy(key, idx, 'carrier_name', e.target.value)} className="h-7 text-xs bg-white flex-1 min-w-[100px]" />
                        <Input type="text" placeholder="Policy #" disabled={!canEdit} value={p.policy_no || ''} onChange={(e) => updatePolicy(key, idx, 'policy_no', e.target.value)} className="h-7 text-xs bg-white w-28" />
                        <span className="text-[10px] text-gray-400 shrink-0">Start</span>
                        <Input type="date" disabled={!canEdit} value={p.start_date || ''} onChange={(e) => updatePolicy(key, idx, 'start_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                        <span className="text-[10px] text-gray-400 shrink-0">End</span>
                        <Input type="date" disabled={!canEdit} value={p.end_date || ''} onChange={(e) => updatePolicy(key, idx, 'end_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                        {canEdit && (
                          <button type="button" onClick={() => removePolicy(key, idx)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Previous policies */}
                {currentPolicy && historicalPolicies.length > 0 && (
                  <div className="mx-3 mb-2">
                    <button
                      type="button"
                      onClick={() => setExpandedTypes((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 w-full"
                      data-testid={`insurance-${key}-history-toggle`}
                    >
                      <ChevronRight className={`w-3 h-3 transition-transform ${expandedTypes[key] ? 'rotate-90' : ''}`} />
                      Previous Policies ({historicalPolicies.length})
                    </button>
                    {expandedTypes[key] && (
                      <div className="mt-1.5 space-y-1.5">
                        {historicalPolicies.map((p) => {
                          const idx = list.indexOf(p);
                          return (
                            <div key={p.id || idx} className="flex items-center gap-2 p-2 rounded border border-gray-200 bg-white">
                              <Input type="text" placeholder="Carrier" disabled={!canEdit} value={p.carrier_name || ''} onChange={(e) => updatePolicy(key, idx, 'carrier_name', e.target.value)} className="h-7 text-xs bg-white flex-1 min-w-[100px]" />
                              <Input type="text" placeholder="Policy #" disabled={!canEdit} value={p.policy_no || ''} onChange={(e) => updatePolicy(key, idx, 'policy_no', e.target.value)} className="h-7 text-xs bg-white w-28" />
                              <span className="text-[10px] text-gray-400 shrink-0">Start</span>
                              <Input type="date" disabled={!canEdit} value={p.start_date || ''} onChange={(e) => updatePolicy(key, idx, 'start_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                              <span className="text-[10px] text-gray-400 shrink-0">End</span>
                              <Input type="date" disabled={!canEdit} value={p.end_date || ''} onChange={(e) => updatePolicy(key, idx, 'end_date', e.target.value)} className="h-7 text-xs bg-white w-36 shrink-0" />
                              {canEdit && (
                                <button type="button" onClick={() => removePolicy(key, idx)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {selectedCompany && canEdit && (
            <div className="mt-3 flex justify-end">
              <Button type="button" onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600" data-testid="insurance-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Policies</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
